import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Copy, ExternalLink, RefreshCw, Save, Smartphone, AlertTriangle, ChevronDown } from 'lucide-react';

// Admin > Ads: Google AdSense in 5 steps. The admin pastes whatever AdSense shows; the IDs are picked out.

export function extractPublisherId(text) {
  const m = /(?:ca-)?pub-(\d{10,20})/i.exec(text || '');
  return m ? `ca-pub-${m[1]}` : '';
}

export function extractAdSlot(text) {
  const m = /data-ad-slot\s*=\s*["']?(\d{6,15})|^\s*(\d{6,15})\s*$/im.exec(text || '');
  return m ? (m[1] || m[2]) : '';
}

const ADSENSE = 'https://adsense.google.com/';

function Check({ ok, children, testid }) {
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${ok ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-slate-600 bg-slate-700/30 text-slate-300'}`} data-testid={testid}>
      {ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <Circle className="w-4 h-4 shrink-0" />}
      <span className="min-w-0">{children}</span>
    </div>
  );
}

function Step({ n, title, done, children }) {
  return (
    <div className="flex gap-3">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${done ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>
        {done ? '✓' : n}
      </div>
      <div className="flex-1 min-w-0 space-y-2 pb-1">
        <p className="font-semibold text-white">{title}</p>
        {children}
      </div>
    </div>
  );
}

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Copied');
  } catch (e) {
    toast.info(text);
  }
};

export function AdSenseSetup() {
  const [form, setForm] = useState({ client: '', enabled: false, banner: '', show_banners: true, extra: '' });
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [advanced, setAdvanced] = useState(false);

  const loadStatus = useCallback(async () => {
    setChecking(true);
    try {
      const res = await adminAPI.getAdSenseStatus();
      setStatus(res.data);
    } catch (e) {
      /* keep old status */
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getAdConfig();
        const g = res.data?.google_adsense || {};
        setForm({
          client: g.client_id || '',
          enabled: !!g.enabled,
          banner: g.banner_slot_id || '',
          show_banners: g.show_banners !== false,
          extra: g.ads_txt_extra || '',
        });
      } catch (e) {
        toast.error('Could not load the AdSense settings');
      }
    })();
    loadStatus();
  }, [loadStatus]);

  const pub = extractPublisherId(form.client);
  const slot = extractAdSlot(form.banner);
  const site = status?.site_url || 'https://socraquest.sk';
  const domain = site.replace(/^https?:\/\//, '');
  const savedPub = status?.client_id || '';
  const adsTxtOk = !!status?.ads_txt?.ok;
  const live = !!status?.enabled;

  const save = async (extra = {}) => {
    if (form.client.trim() && !pub) {
      toast.error('No publisher ID found. It looks like pub-1234567890123456.');
      return;
    }
    setSaving(true);
    try {
      const res = await adminAPI.updateAdConfig({
        google_adsense: {
          client_id: pub,
          enabled: form.enabled,
          banner_slot_id: slot,
          show_banners: form.show_banners,
          ads_txt_extra: form.extra,
          ...extra,
        },
      });
      const errs = res.data?.ads_txt?.errors || [];
      if (errs.length) toast.error(`Saved, but ads.txt could not be written: ${errs[0]}`);
      else toast.success('AdSense settings saved');
      setForm((f) => ({ ...f, client: pub, banner: slot, ...(extra.enabled !== undefined ? { enabled: extra.enabled } : {}) }));
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saving failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50 space-y-5" data-testid="adsense-setup">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Google AdSense (website)</h2>
          <p className="text-sm text-slate-400 mt-1">Paste what AdSense shows you - we do the technical part (ads.txt, site code).</p>
        </div>
        <Button onClick={loadStatus} variant="outline" size="sm" disabled={checking}
          className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white" data-testid="adsense-recheck">
          <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} /> Check again
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-2">
        <Check ok={!!savedPub} testid="adsense-check-id">{savedPub ? `Publisher ID ${savedPub}` : 'Publisher ID not saved yet'}</Check>
        <Check ok={adsTxtOk} testid="adsense-check-adstxt">{adsTxtOk ? 'ads.txt is online' : (status?.ads_txt?.detail || 'ads.txt not online yet')}</Check>
        <Check ok={live} testid="adsense-check-live">{live ? 'Ads are ON on the website' : 'Ads are OFF on the website'}</Check>
      </div>

      <div className="space-y-5">
        <Step n={1} title="Open your AdSense account and add the site" done={!!savedPub}>
          <p className="text-sm text-slate-400">Sign in with the Google account that should receive the money. In <b>Sites</b> click <b>Add site</b> and enter <b className="text-white">{domain}</b>.</p>
          <a href={ADSENSE} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-teal-300 hover:text-teal-200">
            Open Google AdSense <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </Step>

        <Step n={2} title="Paste your publisher ID" done={!!savedPub}>
          <p className="text-sm text-slate-400">In AdSense: <b>Account &gt; Settings &gt; Account information</b> (pub-…). You can also paste the whole AdSense code or the ads.txt line.</p>
          <Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}
            placeholder="pub-1234567890123456" className="bg-slate-700/50 border-slate-600 text-white font-mono" data-testid="adsense-client-input" />
          {form.client.trim() && (
            <p className={`text-xs ${pub ? 'text-emerald-300' : 'text-amber-300'}`} data-testid="adsense-client-found">
              {pub ? `Found: ${pub}` : 'No publisher ID found in this text'}
            </p>
          )}
          <Button onClick={() => save()} disabled={saving || !pub} className="bg-teal-600 hover:bg-teal-700" data-testid="adsense-save-id">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save and publish ads.txt'}
          </Button>
        </Step>

        <Step n={3} title="Verify the site in AdSense" done={adsTxtOk}>
          <p className="text-sm text-slate-400">
            The site already has the ads.txt file and the AdSense code. In AdSense open <b>Sites &gt; {domain}</b>, choose
            <b> ads.txt snippet</b> (or <b>AdSense code snippet</b>), click <b>Verify</b>, then <b>Request review</b>. Google's review takes a few days.
          </p>
          {status?.ads_txt_line && (
            <div className="flex items-center gap-2 rounded-lg bg-slate-900/60 border border-slate-700 px-3 py-2">
              <code className="text-xs text-teal-200 break-all flex-1">{status.ads_txt_line}</code>
              <button type="button" onClick={() => copy(status.ads_txt_line)} className="text-slate-400 hover:text-white" aria-label="Copy ads.txt line"><Copy className="w-4 h-4" /></button>
            </div>
          )}
          <a href={`${site}/ads.txt`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-teal-300 hover:text-teal-200">
            Open {domain}/ads.txt <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </Step>

        <Step n={4} title="Turn on the EU consent message (needed for visitors from Slovakia/EU)" done={false}>
          <p className="text-sm text-slate-400">
            In AdSense: <b>Privacy &amp; messaging &gt; European regulations &gt; Create message</b>, select {domain}, then <b>Publish</b>.
            Without it Google shows only limited ads to EU visitors.
          </p>
        </Step>

        <Step n={5} title="Show the ads" done={live}>
          <p className="text-sm text-slate-400">
            In AdSense turn on <b>Auto ads</b> for {domain} (<b>Ads &gt; By site</b>) - Google then picks good places itself.
            Switch on here when AdSense says the site is <b>Ready</b>.
          </p>
          <div className="flex items-center gap-3">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="adsense-enabled" className="data-[state=unchecked]:bg-slate-600 data-[state=checked]:bg-teal-500" />
            <Label className="text-slate-300">Show Google ads on the website: <b className={form.enabled ? 'text-teal-300' : 'text-slate-400'}>{form.enabled ? 'ON' : 'OFF'}</b></Label>
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300">Banner ad unit (optional)</Label>
            <p className="text-xs text-slate-500">AdSense: <b>Ads &gt; By ad unit &gt; Display ads</b>, create a responsive unit and paste its code or number. Shown at the bottom of the Home and Results pages.</p>
            <Input value={form.banner} onChange={(e) => setForm({ ...form, banner: e.target.value })}
              placeholder="1234567890 or the ad unit code" className="bg-slate-700/50 border-slate-600 text-white font-mono" data-testid="adsense-banner-input" />
            {form.banner.trim() && <p className={`text-xs ${slot ? 'text-emerald-300' : 'text-amber-300'}`}>{slot ? `Ad unit: ${slot}` : 'No ad unit number found'}</p>}
            <div className="flex items-center gap-3 pt-1">
              <Switch checked={form.show_banners} onCheckedChange={(v) => setForm({ ...form, show_banners: v })} className="data-[state=unchecked]:bg-slate-600 data-[state=checked]:bg-teal-500" />
              <Label className="text-slate-300 text-sm">Show the banner unit: <b className={form.show_banners ? 'text-teal-300' : 'text-slate-400'}>{form.show_banners ? 'ON' : 'OFF'}</b></Label>
            </div>
          </div>
          <Button onClick={() => save()} disabled={saving || (!pub && form.enabled)} className="bg-blue-600 hover:bg-blue-700" data-testid="adsense-save">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
          </Button>
          {form.enabled && !pub && <p className="text-xs text-amber-300">Add the publisher ID first (step 2).</p>}
        </Step>
      </div>

      <button type="button" onClick={() => setAdvanced(!advanced)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white">
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${advanced ? 'rotate-180' : ''}`} /> Other ad networks in ads.txt (advanced)
      </button>
      {advanced && (
        <div className="space-y-2">
          <textarea value={form.extra} onChange={(e) => setForm({ ...form, extra: e.target.value })} rows={4}
            placeholder="one line per network, e.g. example.com, 12345, RESELLER"
            className="w-full rounded-lg bg-slate-700/50 border border-slate-600 text-white font-mono text-xs p-2" data-testid="adsense-extra" />
          <Button onClick={() => save()} disabled={saving} variant="outline" size="sm" className="bg-transparent border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white">Save ads.txt</Button>
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="adsense-apps-note">
        <Smartphone className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          <b>Android / iOS apps:</b> Google does not allow AdSense inside apps, so the apps never show AdSense ads.
          They show your own ads from <b>Rewarded Ads</b> above. Google ads in the apps need <b>AdMob</b> (added with an app update).
        </span>
      </div>
      {status?.ads_txt && !status.ads_txt.ok && savedPub && (
        <div className="flex items-start gap-2 text-xs text-slate-400">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" />
          <span>{status.ads_txt.detail} ({status.ads_txt.url})</span>
        </div>
      )}
    </div>
  );
}
